import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { breadcrumbNavigationActions, selectBreadcrumbs } from './breadcrumbNavigationSlice';

interface UseTrackBreadcrumbsParams {
    section: string;
    pathMatcher: (path: string) => boolean;
}

/**
 * Hook to track breadcrumb navigation for a specific section.
 * Maintains a list of visited paths that match the provided pathMatcher in Redux state.
 *
 * @param section - Unique identifier for this breadcrumb section
 * @param pathMatcher - Function that returns true if a path should be tracked
 *
 * Navigation behavior:
 * - PUSH: Adds path to breadcrumb list if pathMatcher returns true
 * - POP: Removes the last path from the breadcrumb list
 * - REPLACE: Updates the most recent breadcrumb to the new path (adds it if none exist yet)
 * - When landing on a non-matching path, clears the breadcrumb list for that section
 */
export function useTrackBreadcrumbs({ section, pathMatcher }: UseTrackBreadcrumbsParams): void {
    const location = useLocation();
    const navigationType = useNavigationType();
    const dispatch = useDispatch();
    const breadcrumbs = useSelector(selectBreadcrumbs(section));
    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    const previousPathRef = useRef<string | null>(null);

    useEffect(() => {
        const currentPath = location.pathname;
        const matchesPattern = pathMatcher(currentPath);
        const previousPath = previousPathRef.current;
        const pathChanged = previousPath !== currentPath;

        if (!matchesPattern) {
            // Clear breadcrumbs when navigating to a non-matching path
            if (breadcrumbs.length > 0) {
                dispatch(breadcrumbNavigationActions.clearBreadcrumbs({ section }));
            }
            if (pathChanged) {
                previousPathRef.current = currentPath;
            }
            return;
        }

        if (!pathChanged) {
            return;
        }

        if (navigationType === 'PUSH') {
            // Add new path to breadcrumbs
            if (lastBreadcrumb !== currentPath) {
                dispatch(breadcrumbNavigationActions.addBreadcrumb({ section, path: currentPath }));
            }
        } else if (navigationType === 'POP') {
            // Remove last path from breadcrumbs when history moved back/forward
            if (breadcrumbs.length > 0 && lastBreadcrumb !== currentPath) {
                dispatch(breadcrumbNavigationActions.removeBreadcrumb({ section }));
            }
        } else if (navigationType === 'REPLACE') {
            if (breadcrumbs.length === 0 || lastBreadcrumb !== currentPath) {
                dispatch(breadcrumbNavigationActions.replaceLastBreadcrumb({ section, path: currentPath }));
            }
        }

        previousPathRef.current = currentPath;
    }, [location.pathname, navigationType, section, pathMatcher, breadcrumbs.length, lastBreadcrumb, dispatch]);
}
